import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SuitDivider } from './SuitDivider';
interface OfferingCardProps {
  suit: string;
  suitColor: string;
  title: string;
  description: string;
  index: number;
}
function OfferingCard({
  suit,
  suitColor,
  title,
  description,
  index
}: OfferingCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.7,
    delay: index * 0.15,
    ease: 'easeOut'
  }} className="relative p-8 md:p-10" data-id="element-43">
      {/* Ornate border */}
      <div className="absolute inset-0 border border-goldenrod/30" data-id="element-44" />
      <div className="absolute inset-2 border border-goldenrod/15" data-id="element-45" />

      {/* Corner ornaments */}
      <div className="deco-corner-tl" data-id="element-46" />
      <div className="deco-corner-tr" data-id="element-47" />
      <div className="deco-corner-bl" data-id="element-48" />
      <div className="deco-corner-br" data-id="element-49" />

      {/* Large decorative suit */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6" data-id="element-50">
        <span className={`text-5xl md:text-6xl ${suitColor} opacity-15 select-none font-body`} aria-hidden="true" data-id="element-51">
          {suit}
        </span>
      </div>

      {/* Suit icon */}
      <div className={`text-3xl ${suitColor} mb-5 select-none`} aria-hidden="true" data-id="element-52">
        {suit}
      </div>

      <h3 className="font-display text-xl md:text-2xl text-ivory uppercase tracking-[0.12em] mb-4 pr-10" data-id="element-53">
        {title}
      </h3>

      <p className="font-body text-ivory/80 text-lg leading-relaxed" data-id="element-54">
        {description}
      </p>
    </motion.div>;
}
export function Gallery() {
  return <section className="relative bg-deep-green py-24 md:py-32" data-id="element-55">
      <div className="max-w-6xl mx-auto px-6" data-id="element-56">
        {/* Section header */}
        <div className="text-center mb-16" data-id="element-57">
          <motion.h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-goldenrod uppercase tracking-[0.3em] mb-4" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-58">
            The Gallery
          </motion.h2>
          <motion.p className="font-body text-goldenrod/70 italic text-lg tracking-wide" initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-59">
            Capabilities &amp; Services
          </motion.p>
          <SuitDivider className="mt-8 max-w-md mx-auto" data-id="element-60" />
        </div>

        {/* 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10" data-id="element-61">
          <OfferingCard suit="♠" suitColor="text-goldenrod" title="Strategy Engine Development" description="Custom AI agents engineered for specific game variants. Our engines are built from first principles using counterfactual regret minimization, producing strategies that approach theoretical optimality." index={0} data-id="element-62" />
          <OfferingCard suit="♥" suitColor="text-crimson" title="Opponent Modeling Systems" description="Real-time behavioral analysis and exploitation frameworks. Our systems construct dynamic opponent models from observed play, identifying and capitalizing on deviations from equilibrium strategy." index={1} data-id="element-63" />
          <OfferingCard suit="♣" suitColor="text-goldenrod" title="Simulation Environments" description="High-fidelity game simulations for strategy testing and validation. Run millions of hands against diverse opponent archetypes to stress-test strategies before deployment in live environments." index={2} data-id="element-64" />
          <OfferingCard suit="♦" suitColor="text-goldenrod" title="Research &amp; Analysis" description="Game-theoretic research and strategic consulting. We provide deep analytical insights into game dynamics, equilibrium structures, and exploitative opportunities across casino environments." index={3} data-id="element-65" />
        </div>
      </div>
    </section>;
}